import { isPosthogEnabled } from '@lib/observability/utils/is-posthog-enabled';

import type { AnalyticsProvider } from '../../types/analytics-provider';

/**
 * PostHogAnalyticsProvider — identifies users in PostHog for client-side analytics.
 *
 * Environment: client-only (requires posthog-js to be initialized and loaded).
 * Used by: observability/analytics/tracking-provider.tsx
 *
 * To replace this provider: implement AnalyticsProvider in a new file and swap
 * the usage in tracking-provider.tsx. No other files need to change.
 */
export class PostHogAnalyticsProvider implements AnalyticsProvider {
  isEnabled(): boolean {
    return isPosthogEnabled();
  }
  captureEvent(eventName: string, properties?: Record<string, unknown>): void {
    if (!this.isEnabled()) return;
    if (typeof window === 'undefined') return;

    import('posthog-js')
      .then(({ default: posthog }) => {
        if (posthog.__loaded) {
          posthog.capture(eventName, properties);
        }
      })
      .catch(() => {
        // Silently ignore — analytics must never throw
      });
  }
  identify(
    userId: string,
    email: string,
    traits?: Record<string, unknown>
  ): void {
    if (!this.isEnabled()) return;
    if (typeof window === 'undefined') return;

    import('posthog-js')
      .then(({ default: posthog }) => {
        if (posthog.__loaded) {
          posthog.identify(userId, {
            email,
            ...(traits ?? {}),
          });
        }
      })
      .catch(() => {
        // Silently ignore — analytics must never throw
      });
  }
}
